import {
  DollarOutlined,
  HistoryOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { Form, Tabs } from "antd";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ClientWithStorage } from "~/services/client/client.queries";
import { BillingSubNav } from "../subnav";
import {
  StorageSettingsModal,
  InvoicePreviewModal,
  GracePeriodTab,
  BillingHistoryTab,
  StorageSettingsTab,
} from "./components";
import { PreviewData } from "./types";

export const StorageInvoicingComponent: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedClient, setSelectedClient] =
    useState<ClientWithStorage | null>(null);
  const [isSettingsModalVisible, setIsSettingsModalVisible] =
    useState<boolean>(false);
  const [isPreviewModalVisible, setIsPreviewModalVisible] =
    useState<boolean>(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [form] = Form.useForm();

  // Valid tab keys
  const validTabs = useMemo(() => ["settings", "history", "grace-period"], []);

  // Get activeTab from URL query params, fallback to "settings"
  const getActiveTabFromURL = useCallback((): string => {
    const tabFromURL = searchParams.get("tab");
    return validTabs.includes(tabFromURL || "")
      ? (tabFromURL as string)
      : "settings";
  }, [searchParams, validTabs]);

  const [activeTab, setActiveTab] = useState<string>(getActiveTabFromURL());

  // Sync activeTab with URL on mount and when URL changes
  useEffect(() => {
    const tabFromURL = getActiveTabFromURL();
    if (tabFromURL !== activeTab) {
      setActiveTab(tabFromURL);
    }
  }, [activeTab, getActiveTabFromURL, searchParams]);

  // Handle tab change and update URL
  const handleTabChange = (key: string) => {
    setActiveTab(key);

    // Update URL query params
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("tab", key);
    setSearchParams(newSearchParams, { replace: true });
  };

  // Handle modal callbacks
  const handleSettingsSuccess = () => {
    // Modal success handled internally by components
  };

  const handlePreviewSuccess = () => {
    // Modal success handled internally by components
  };

  return (
    <div>
      <BillingSubNav activeKey="storage" />

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: "settings",
            label: (
              <span className="flex items-center gap-2">
                <DollarOutlined />
                Storage Settings
              </span>
            ),
            children: (
              <StorageSettingsTab
                onPreviewInvoice={(previewData) => {
                  setPreviewData(previewData);
                  setIsPreviewModalVisible(true);
                }}
                onEditSettings={(client) => {
                  setSelectedClient(client);
                  form.setFieldsValue({
                    storageRate: client.storageRate,
                    includeInsurance: client.includeInsurance,
                    insuranceRate: client.insuranceRate
                      ? client.insuranceRate * 100
                      : 1.0,
                  });
                  setIsSettingsModalVisible(true);
                }}
              />
            ),
          },
          {
            key: "history",
            label: (
              <span className="flex items-center gap-2">
                <HistoryOutlined />
                Billing History
              </span>
            ),
            children: <BillingHistoryTab />,
          },
          {
            key: "grace-period",
            label: (
              <span className="flex items-center gap-2">
                <ClockCircleOutlined />
                Grace Period
              </span>
            ),
            children: <GracePeriodTab />,
          },
        ]}
      />

      <StorageSettingsModal
        visible={isSettingsModalVisible}
        onClose={() => setIsSettingsModalVisible(false)}
        selectedClient={selectedClient}
        form={form}
        onSuccess={handleSettingsSuccess}
      />

      <InvoicePreviewModal
        visible={isPreviewModalVisible}
        onClose={() => setIsPreviewModalVisible(false)}
        previewData={previewData}
        onSuccess={handlePreviewSuccess}
      />
    </div>
  );
};

export default StorageInvoicingComponent;
